import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import SeoScript from "@/components/feature/SeoScript";
import AnimeNavbar from "@/components/feature/AnimeNavbar";
import AnimeFooter from "@/components/feature/AnimeFooter";
import usePageTitle from "@/hooks/usePageTitle";
import { episodes, siteName } from "@/mocks/anime";

const heroBg =
  "https://readdy.ai/api/search-image?query=anime%20illustration%20of%20a%20sunrise%20over%20a%20quiet%20campus%20with%20a%20lone%20figure%20on%20a%20hill%20facing%20the%20horizon%2C%20large%20dark%20amber%20and%20brown%20areas%20for%20text%20contrast%2C%20ink%20wash%20style%2C%20hopeful%20yet%20bittersweet%20cinematic%20manga%20aesthetic%2C%20clean%20composition&width=1600&height=600&seq=ending-hero&orientation=landscape";

function EndArticle() {
  const highlights = [
    { title: "伏笔的最终回收", text: "前几话看似随手带过的细节，在结局几乎全部有了呼应——例如旧照片、一句台词、甚至某个不起眼的路人。回看时会发现作者早已埋好线。" },
    { title: "师徒关系的落点", text: "结局没有给这段关系一个非黑即白的定义，而是让主角在告别中学会承前启后。这份克制的处理，恰恰是整部作品最动人的地方。" },
    { title: "开放式的留白", text: "主角未来的方向并未完全写死，留下了足够的想象空间。这样的处理让结局既有收束的踏实感，又不失余韵。" },
  ];

  const latest = episodes.slice(-3);

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">《秘密教学动漫》结局解析</h2>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-secondary-700">结局解析</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i>约 8 分钟阅读</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i>含剧透</span>
            </div>

            <p className="mt-6 text-sm leading-7 text-foreground-700">
              作为一部以「秘密」与「选择」为骨的作品，《秘密教学动漫》的结局并没有停留在简单的善恶对决上，而是把镜头对准了主角一路走来所经历的和解——与同伴的和解、与过往的和解，最终是与自己的和解。整段收尾情感浓度很高，同时保留了足够克制的留白。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              结局最让人印象深刻的一点，是它没有急着给所有问题一个标准答案。主线谜团得到了清晰交代，但人物的未来仍是开放的。正是这种「有收有放」的节奏，让结尾既有满足感，又值得反复回味。
            </p>

            <h3 className="mt-8 text-base font-medium text-foreground-900">结局的三大亮点</h3>
            <div className="mt-4 space-y-4">
              {highlights.map((h, idx) => (
                <div key={h.title} className="flex gap-4 rounded-md bg-background-100 p-4">
                  <span className="font-heading text-xl text-primary-500">{String(idx + 1).padStart(2, "0")}</span>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900">{h.title}</h4>
                    <p className="mt-1.5 text-sm leading-6 text-foreground-600">{h.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="mt-8 text-base font-medium text-foreground-900">看完结局再回看的细节</h3>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              如果你已经看过结局，建议再回头重看前几话。你会发现主角第一次与引路人相遇时那句看似随意的嘱托、同伴偶然提到的一句往事，甚至是背景里一闪而过的某个身影，都与结局形成了严丝合缝的呼应。这些藏在细节里的彩蛋，正是这部作品值得二刷的魅力所在。
            </p>
          </div>
        </article>

        <aside className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="text-base font-medium text-foreground-900">相关推荐</h3>
            <ul className="mt-4 space-y-3">
              {latest.map((ep) => (
                <li key={ep.ep}>
                  <Link to="/episodes" className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100 cursor-pointer">
                    <span className="font-heading text-primary-500">EP.{ep.ep}</span>
                    <span className="text-sm text-foreground-700 group-hover:text-primary-700">{ep.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h3 className="text-base font-medium text-accent-900">还没看完？</h3>
            <p className="mt-2 text-sm leading-6 text-accent-800">
              这份解析默认你已经知道结局。如果想从头慢慢追，建议先看完整的分集介绍，再回来看这篇，体验会更完整。
            </p>
            <Link to="/episodes" className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer">
              查看分集介绍
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </div>
    </section>
  );
}

export default function Ending() {
  usePageTitle("结局解析");
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://mysteryplot.github.io/" },
          { "@type": "ListItem", position: 2, name: "结局解析" },
        ],
      },
      {
        "@type": "Article",
        headline: "秘密教学动漫结局解析：伏笔回收与开放式留白",
        name: "秘密教学动漫结局解析",
        url: "https://mysteryplot.github.io/ending",
        author: { "@type": "Organization", name: siteName },
        publisher: { "@type": "Organization", name: siteName },
      },
    ],
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SeoScript data={jsonLd} />
      <AnimeNavbar />
      <main className="flex-1">
        <PageHero
          tag={siteName}
          title="秘密教学动漫结局解析"
          desc="主线谜团的最终收束、师徒羁绊的落点与开放式结局的留白。这篇解析带你回看每一处被埋下的伏笔与彩蛋。"
          bg={heroBg}
        />
        <nav className="border-b border-background-200 bg-background-100" aria-label="面包屑">
          <div className="mx-auto flex max-w-7xl items-center gap-1 px-4 py-3 text-sm md:px-6">
            <Link to="/" className="text-foreground-500 hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-800">结局解析</span>
          </div>
        </nav>
        <EndArticle />
      </main>
      <AnimeFooter />
    </div>
  );
}